import React from "react";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import type { BulkSendBatchPhase } from "./useBulkSendBatchSubmitter";

/**
 * Shared progress / error view used by every CFR bulk-send dialog while
 * the staged-batch lifecycle is running.
 *
 * The dialog renders this whenever `phase.kind !== "idle"`. The error
 * branch must show the upstream message verbatim (spec \u00a77.3 \u2014 no
 * toast collapsing) and offers a single Close button. The mid-flight
 * branches render a label + chunk-level detail + a coarse progress bar.
 */
export function BulkSendBatchProgressView({
    phase,
    onClose,
}: {
    phase: Exclude<BulkSendBatchPhase, { kind: "idle" }>;
    onClose: () => void;
}) {
    if (phase.kind === "error") {
        return (
            <div className="grid gap-4 py-6">
                <Alert variant="destructive">
                    <AlertTitle>{errorTitleForPhase(phase.phase)}</AlertTitle>
                    <AlertDescription className="whitespace-pre-wrap break-words">
                        {phase.message}
                    </AlertDescription>
                </Alert>
                <DialogFooter>
                    <Button type="button" onClick={onClose}>Close</Button>
                </DialogFooter>
            </div>
        );
    }

    let label = "Working...";
    let detail: React.ReactNode = null;
    let percent = 0;

    if (phase.kind === "creating") {
        label = "Creating batch...";
        percent = 10;
    } else if (phase.kind === "uploading") {
        label = "Uploading items...";
        detail = (
            <div className="text-sm text-muted-foreground">
                Chunk {phase.currentChunk} of {phase.totalChunks} | {phase.uploadedItems} of {phase.totalItems} items uploaded
            </div>
        );
        percent = phase.totalItems === 0
            ? 0
            : 10 + Math.round((phase.uploadedItems / phase.totalItems) * 80);
    } else if (phase.kind === "finalizing") {
        label = "Finalizing batch...";
        percent = 95;
    }

    return (
        <div className="grid gap-4 py-8">
            <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <div className="text-lg font-semibold">{label}</div>
            </div>
            {detail}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${percent}%` }}
                />
            </div>
            <div className="text-xs text-muted-foreground text-center">
                Please leave this dialog open; you'll be redirected to the batch status page when finalize completes.
            </div>
        </div>
    );
}

function errorTitleForPhase(phase: "creating" | "uploading" | "finalizing" | "local_validation"): string {
    switch (phase) {
        case "creating":         return "Failed to create batch";
        case "uploading":        return "Failed while uploading items";
        case "finalizing":       return "Failed to finalize batch";
        case "local_validation": return "Invalid local payload";
    }
}
